const { useEffect, useState } = require('react');
const { context } = require('app-context');
const { v4: uuid } = require('uuid');
const { default: Button } = require('@mui/material/Button');
const { default: TextField } = require('@mui/material/TextField');
const { default: Checkbox } = require('@mui/material/Checkbox');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: Styled } = require('styled-components');

const internals = {};

module.exports = function GameLibraryAdmin() {

    const [data, setData] = useState(null);
    const [game, setGame] = useState(null);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState('');
    const { Panel, Fields, kinds } = internals;
    useEffect(() => {

        let active = true;
        context.api.nearpeer.nerpy.triviaGamesAdmin?.().then((value) => { if (active) { setData(value); } }).catch(() => {});
        return () => { active = false; };
    }, []);
    if (!data) { return null; }

    const update = (key, value) => setGame((current) => ({ ...current, [key]: value }));
    const save = async (schedule) => {

        if (busy) { return; }
        setBusy(true);
        setStatus('');
        try {
            if (schedule) {
                const saved = await context.api.nearpeer.nerpy.saveTriviaSchedule(data.schedule);
                setData((current) => ({ ...current, schedule: saved }));
            }
            else {
                const saved = await context.api.nearpeer.nerpy.saveTriviaGame(game);
                setData((current) => ({ ...current, games: current.games.some((g) => g.key === saved.key) ? current.games.map((g) => g.key === saved.key ? saved : g) : [...current.games, saved] }));
                setGame(saved);
            }

            setStatus('Saved. No messages were sent.');
        }
        catch { setStatus('Not saved. Check the fields, artwork readiness, and whether another game occupies that week. Your edits are still here.'); }
        finally { setBusy(false); }
    };
    const schedule = (key, value) => setData((current) => ({ ...current, schedule: { ...current.schedule, [key]: value } }));

    return <Panel><summary>Game library and school schedule</summary><Fields>
        <TextField label='School start Monday (YYYY-MM-DD)' value={data.schedule.startMonday || ''} disabled={busy} onChange={(event) => schedule('startMonday', event.target.value || null)} />
        <TextField label='School time zone' value={data.schedule.timeZone} disabled={busy} onChange={(event) => schedule('timeZone', event.target.value)} helperText='For example, America/Los_Angeles. Releases happen at local midnight on Monday.' />
        <FormControlLabel label='Use the weekly release schedule' control={<Checkbox checked={data.schedule.enabled} disabled={busy} onChange={(event) => schedule('enabled', event.target.checked)} />} />
        <Button disabled={busy} onClick={() => save(true)}>Save schedule</Button>
        <TextField select SelectProps={{ native: true }} label='Choose a game to edit' value={game?.key || ''} disabled={busy} onChange={(event) => setGame(data.games.find((g) => g.key === event.target.value) || null)}>
            <option value=''>Select a game</option>{game && !data.games.some((g) => g.key === game.key) && <option value={game.key}>New game</option>}{data.games.map((g) => <option key={g.key} value={g.key}>{g.title}</option>)}
        </TextField>
        <Button disabled={busy} onClick={() => setGame({ key: `game-${uuid()}`, title: '', kind: 'trivia', rules: '', introduction: '', invitation: '', closing: '', featureWeek: null, enabled: false })}>Add game</Button>
        {game && <>
            <TextField label='Game title' value={game.title} disabled={busy} onChange={(event) => update('title', event.target.value)} />
            <TextField select SelectProps={{ native: true }} label='Game type' value={game.kind} disabled={busy} onChange={(event) => update('kind', event.target.value)}>{Object.entries(kinds).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</TextField>
            {['rules', 'introduction', 'invitation', 'closing'].map((key) => <TextField key={key} multiline label={key[0].toUpperCase() + key.slice(1)} value={game[key] || ''} disabled={busy} onChange={(event) => update(key, event.target.value)} helperText={key === 'introduction' ? 'A little mischief, a small story, and an invitation. Use {name} for the school’s assistant name.' : ''} />)}
            <TextField label='Feature week (blank for always available)' type='number' value={game.featureWeek ?? ''} disabled={busy} onChange={(event) => update('featureWeek', event.target.value === '' ? null : Number(event.target.value))} inputProps={{ min: 1, max: 104 }} />
            <FormControlLabel label='Game enabled' control={<Checkbox checked={game.enabled} disabled={busy} onChange={(event) => update('enabled', event.target.checked)} />} />
            <Button disabled={busy} onClick={() => save(false)}>Save game</Button>
        </>}
        <p role='status'>{status}</p>
    </Fields></Panel>;
};

internals.kinds = { 'emoji-movie': 'Emoji movies', 'emoji-tv': 'Emoji TV', 'emoji-band': 'Emoji bands', 'emoji-song': 'Emoji songs', trivia: 'Trivia', 'art-book': 'Artwork and book titles' };
internals.Panel = Styled.details`padding: 16px; margin-bottom: 24px; border: 1px solid ${({ theme }) => theme.palette?.divider || '#ddd'}; border-radius: 8px; summary { cursor: pointer; font-weight: bold; }`;
internals.Fields = Styled.div`display: flex; flex-direction: column; gap: 12px; margin-top: 12px;`;
